import { Component, useEffect, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import {
  BRAND_LOGO,
  Button,
  CustomerShell,
  LoadingState,
  UnavailableSection,
} from '@beautyburst/ui';
import type { CustomerApiClient } from './api-client.ts';
import { DesktopNav } from './navigation.tsx';
import { Home, type HomepageSections } from './screens/home.tsx';
import type { NearbyStores } from './screens/home.tsx';
import { NotFoundScreen } from './screens/not-found.tsx';
import { resolveRoute } from './routes.ts';
import type { RouteContract, RouteMatch } from './routes.ts';
import { customerAnalytics } from './analytics/client.ts';
import { customerAsset, customerHref } from './base-path.ts';
import { MarketSelectionRequired } from './market-selection.tsx';
import { requestWithRetry } from './request-retry.ts';

export interface CustomerActor {
  signedIn: boolean;
  displayName?: string;
}

export interface AppProps {
  path: string;
  client: CustomerApiClient;
  /** Founder-supplied logo asset, injected by the host application. */
  logoSrc?: string;
  flags: ReadonlySet<string>;
  actor: CustomerActor | null;
  selectedMarketId?: string | null;
  markets?: readonly { readonly id: string; readonly name: string }[];
  onMarketChange?: (marketId: string) => void;
  marketChangePending?: boolean;
  marketChangeError?: boolean;
  onRetryMarketChange?: () => void;
  /**
   * An optional server-provided homepage payload avoids a client round trip in
   * hosts that already loaded it. The Home screen remains presentational.
   */
  homepageSections?: HomepageSections;
  leadCaptureSlot?: ReactNode;
  /**
   * Screen modules are composed by the application host as they become
   * available. This module intentionally does not import files owned by other
   * concurrent UI modules.
   */
  renderScreen?: (route: RouteMatch<RouteContract>) => ReactNode;
}

function NotAvailableYet() {
  return (
    <UnavailableSection
      title="Not available yet"
      reason="This area is not available in your current experience."
    />
  );
}

export function SignInPrompt({
  description = 'Sign in to access your account and order history.',
  returnTo,
}: {
  readonly description?: string;
  readonly returnTo?: string;
}) {
  const signInPath =
    returnTo === undefined ? '/sign-in' : `/sign-in?returnTo=${encodeURIComponent(returnTo)}`;
  return (
    <section aria-labelledby="sign-in-prompt-title">
      <h1 id="sign-in-prompt-title">Sign in to continue</h1>
      <p>{description}</p>
      <a href={customerHref(signInPath)}>Sign in</a>
    </section>
  );
}

interface CustomerScreenErrorBoundaryProps {
  readonly children: ReactNode;
}

interface CustomerScreenErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * A route module must never be able to remove the marketplace shell. The header,
 * navigation, and the recovery state remain available if an individual screen
 * has a rendering defect.
 */
export class CustomerScreenErrorBoundary extends Component<
  CustomerScreenErrorBoundaryProps,
  CustomerScreenErrorBoundaryState
> {
  override state: CustomerScreenErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): CustomerScreenErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Customer screen rendering failed.', { error, errorInfo });
  }

  override render(): ReactNode {
    if (this.state.hasError)
      return (
        <section>
          <UnavailableSection
            title="This page needs to be refreshed"
            reason="The Beauty Burst navigation is still available. Please choose another destination or go to the homepage."
          />
          <a href={customerHref('/')}>Go to homepage</a>
        </section>
      );
    return this.props.children;
  }
}

function HomeRoute({
  client,
  initialSections,
  leadCaptureSlot,
  selectedMarketId,
}: {
  client: CustomerApiClient;
  initialSections: HomepageSections | undefined;
  leadCaptureSlot: ReactNode | undefined;
  selectedMarketId: string | null | undefined;
}) {
  const [sections, setSections] = useState<HomepageSections | undefined>(initialSections);
  const [nearbyStores, setNearbyStores] = useState<NearbyStores | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [nearbyStoresFailed, setNearbyStoresFailed] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [nearbyReloadVersion, setNearbyReloadVersion] = useState(0);

  useEffect(() => {
    if (initialSections !== undefined) {
      setSections(initialSections);
      setLoadFailed(false);
      return;
    }

    let active = true;
    setLoadFailed(false);
    setSections(undefined);
    void requestWithRetry(() => client.discovery.homepage(selectedMarketId ?? undefined))
      .then((response) => {
        if (active) {
          setSections(response);
          setLoadFailed(false);
        }
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });

    return () => {
      active = false;
    };
  }, [client, initialSections, reloadVersion, selectedMarketId]);

  useEffect(() => {
    let active = true;
    if (selectedMarketId === null || selectedMarketId === undefined) {
      setNearbyStores([]);
      setNearbyStoresFailed(false);
      return;
    }
    void requestWithRetry(() => client.stores.nearby(selectedMarketId))
      .then((response) => {
        if (active) {
          setNearbyStores(response);
          setNearbyStoresFailed(false);
        }
      })
      .catch(() => {
        if (active) setNearbyStoresFailed(true);
      });
    return () => {
      active = false;
    };
  }, [client, nearbyReloadVersion, selectedMarketId]);

  if (loadFailed) {
    return (
      <section>
        <UnavailableSection
          title="Homepage unavailable"
          reason="We could not load beauty discovery right now. Please try again."
        />
        <Button type="button" onClick={() => setReloadVersion((version) => version + 1)}>
          Retry homepage
        </Button>
      </section>
    );
  }

  if (sections === undefined) return <LoadingState label="Loading homepage" />;
  return (
    <Home
      sections={sections}
      nearbyStores={nearbyStores}
      nearbyStoresFailed={nearbyStoresFailed}
      onRetryNearbyStores={() => setNearbyReloadVersion((version) => version + 1)}
      leadCaptureSlot={leadCaptureSlot}
      client={client}
    />
  );
}

/**
 * The root applies client-side route gating as defence in depth only. The API
 * remains the authorization boundary for every protected operation.
 */
export function App({
  path,
  client,
  logoSrc = customerAsset(BRAND_LOGO.full),
  flags,
  actor,
  selectedMarketId,
  markets = [],
  onMarketChange,
  marketChangePending = false,
  marketChangeError = false,
  onRetryMarketChange,
  homepageSections,
  leadCaptureSlot,
  renderScreen,
}: AppProps) {
  const match = resolveRoute(path);
  const route = match?.route ?? null;
  const [bagItemCount, setBagItemCount] = useState(0);
  const analytics = client.analytics ?? customerAnalytics();

  useEffect(() => {
    analytics.configure({
      enabled: flags.has('analytics.first_party_events'),
      signedIn: actor?.signedIn === true,
    });
    // Attribution is first-party and has no sensitive profile fields. Behavioural
    // events remain opt-in inside the analytics client.
    void analytics.captureInitialAttribution().catch(() => undefined);
    if (route?.screen !== 'Home') return;
    void analytics
      .track({ name: 'homepage.viewed', marketId: selectedMarketId })
      .catch(() => undefined);
  }, [actor?.signedIn, analytics, flags, route?.screen, selectedMarketId]);

  useEffect(() => {
    if (actor?.signedIn !== true) {
      setBagItemCount(0);
      return;
    }
    let active = true;
    void requestWithRetry(() => client.cart.read())
      .then((cart) => {
        if (active)
          setBagItemCount(cart.groups.reduce((total, group) => total + group.itemCount, 0));
      })
      .catch(() => {
        if (active) setBagItemCount(0);
      });
    return () => {
      active = false;
    };
  }, [actor?.signedIn, client]);

  let content: ReactNode;
  const needsMarket =
    route?.screen === 'Home' ||
    route?.screen === 'Search results' ||
    route?.screen === 'Categories' ||
    route?.screen === 'Category' ||
    route?.screen === 'Store front' ||
    route?.screen === 'Stores' ||
    route?.screen === 'Beauty After Hours' ||
    route?.screen === 'Product detail';
  if (match === null || route === null) {
    content = <NotFoundScreen />;
  } else if (route.flag !== null && !flags.has(route.flag)) {
    content = <NotAvailableYet />;
  } else if (route.permission !== null && actor?.signedIn !== true) {
    content = <SignInPrompt returnTo={path} />;
  } else if (needsMarket && (selectedMarketId === null || selectedMarketId === undefined)) {
    content = (
      <MarketSelectionRequired markets={markets} onMarketChange={onMarketChange ?? (() => {})} />
    );
  } else if (route.screen === 'Home') {
    content = (
      <HomeRoute
        client={client}
        initialSections={homepageSections}
        leadCaptureSlot={leadCaptureSlot}
        selectedMarketId={selectedMarketId}
      />
    );
  } else if (renderScreen !== undefined) {
    content = renderScreen(match);
  } else {
    content = (
      <UnavailableSection
        title={route.screen}
        reason="This area is not available in your current experience."
      />
    );
  }

  return (
    <CustomerShell
      logoSrc={logoSrc}
      headerActions={
        <DesktopNav
          activeNavKey={route?.navKey ?? null}
          actor={actor}
          bagItemCount={bagItemCount}
          locationLabel={markets.find((market) => market.id === selectedMarketId)?.name ?? null}
          markets={markets}
          selectedMarketId={selectedMarketId ?? null}
          onMarketChange={onMarketChange}
          marketChangePending={marketChangePending}
          marketChangeError={marketChangeError}
          onRetryMarketChange={onRetryMarketChange}
        />
      }
    >
      <CustomerScreenErrorBoundary key={`${path}:${selectedMarketId ?? 'no-market'}`}>
        {content}
      </CustomerScreenErrorBoundary>
    </CustomerShell>
  );
}
